import { FILTER_VALUE_SEPARATOR } from '@aibees/elephant/constants/filters';
import { FilterExactMatch } from '@aibees/elephant/feature/search-filters/filters/filter-exact-match';
import { useSearchQueryFilter } from '@aibees/elephant/hooks';
import { Icon } from '@aibees/shared/components';
import { KeyCodes } from '@aibees/shared/definition';
import {
  useBoolean,
  useHandleClickOutside,
  useStableReference,
} from '@aibees/shared/hooks';
import { classnames, encodeQueryParamValues } from '@aibees/shared/utils';
import { Box, OutlinedInputProps } from '@mui/material';
import { memo, useCallback, useMemo, useRef, useState } from 'react';

import { uniq } from 'lodash-es';
import { SearchFilterStatusWrapper } from '../../components/search-filter-status-wrapper';
import { renderInput, renderTags } from '../../utils/render-utils';
import { useAutocompleteSearchQuery } from '../autocomplete-tree-filter/hooks/use-autocomplete-search-query';
import { useCommaSeparatedChangeSelection } from '../autocomplete-tree-filter/hooks/use-comma-separated-change-selection';
import { useHasCommaSeparatedInput } from '../autocomplete-tree-filter/hooks/use-has-comma-separated-input';
import { useOnChangeSelection } from '../autocomplete-tree-filter/hooks/use-on-change-selection';
import { useSelectedTreeOptions } from '../autocomplete-tree-filter/hooks/use-selected-tree-options';
import { AutocompleteFilterDropdown } from './components/autocomplete-filter-dropdown';
import * as SX from './sx';
import type { AutocompleteFilterProps as Props } from './types';

const noopCommaSelection = (_values: string[]) => {};

export const AutocompleteFilterContent = memo(
  ({
    className,
    onChange,
    value: customValue,
    exactMatch,
    onClear,
    parentField,
    ...filter
  }: Props) => {
    const selectFields = useMemo(
      () => [filter.field!, ...(parentField ? [parentField] : [])],
      [filter.field, parentField]
    );
    const { params, setParam, removeParam } = useSearchQueryFilter({
      select: selectFields,
    });
    const dropdownState = useBoolean(false);
    const { onToggle: toggleDropdown, onTrue: openDropdown } = dropdownState;
    const selectedValuesRaw = useSelectedTreeOptions(
      filter.field!,
      customValue
    );
    const selectedValues = useStableReference(
      selectedValuesRaw,
      (data: string[]) => data.join(',')
    );
    const { searchInputValue, setSearchInputValue, debouncedSearchQuery } =
      useAutocompleteSearchQuery();

    const [focused, setFocused] = useState(false);

    const componentRef = useRef<HTMLDivElement>(null);
    useHandleClickOutside(componentRef, () => setFocused(false), 0);

    const changeParam = useCallback(
      (value?: string) => {
        if (onChange) onChange(value);
        else setParam(filter.field!, value);
      },
      [filter.field, onChange, setParam]
    );

    const onChangeSelection = useOnChangeSelection(
      filter.field!,
      filter.metadata,
      changeParam,
      setSearchInputValue,
      dropdownState
    );

    const hasComma = useHasCommaSeparatedInput(
      searchInputValue,
      filter.metadata
    );

    const handleCommaSeparatedChangeSelection =
      useCommaSeparatedChangeSelection({
        searchInputValue,
        metadata: filter.metadata,
        onChangeSelection,
        setSearchInputValue,
      });

    const handleSearch = useCallback(
      (searchedValue: string) => {
        const transformer = filter.metadata?.autocomplete?.transformSearchValue;
        const v = transformer ? transformer(searchedValue) : searchedValue;
        setSearchInputValue(v);
      },
      [filter.metadata, setSearchInputValue]
    );

    const handleDeleteChip = useCallback(
      (option: string) => {
        if (onClear) {
          onClear(option);
        } else {
          const seperatedParams =
            params[parentField || filter.field!]?.split(
              FILTER_VALUE_SEPARATOR
            ) || [];
          const draft = seperatedParams.filter((v) => {
            return v !== option && !v.includes(option);
          });
          if (draft.length)
            setParam(filter.field!, encodeQueryParamValues(draft));
          else removeParam(parentField || filter.field!);
        }
      },
      [onClear, params, parentField, filter.field, setParam, removeParam]
    );

    const handleKeyDown: OutlinedInputProps['onKeyDown'] = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (e.key === KeyCodes.backspace) {
          e.stopPropagation();
        }

        if (
          filter.metadata?.autocomplete?.allowCustomValuesByEnter &&
          e.key === KeyCodes.enter &&
          searchInputValue.length > 0
        ) {
          if (filter.metadata?.autocomplete?.multiple) {
            if (hasComma) {
              handleCommaSeparatedChangeSelection(selectedValues);
            } else {
              onChangeSelection(
                uniq([...selectedValues, searchInputValue]),
                searchInputValue
              );
              setSearchInputValue('');
            }
          } else {
            onChangeSelection([searchInputValue], searchInputValue);
          }
        }
      },
      [
        filter.metadata,
        searchInputValue,
        selectedValues,
        onChangeSelection,
        setSearchInputValue,
        hasComma,
        handleCommaSeparatedChangeSelection,
      ]
    );

    const handleClickChevron = useCallback(() => {
      toggleDropdown();
      setSearchInputValue('');
    }, [toggleDropdown, setSearchInputValue]);

    const renderedTags = useMemo(
      () =>
        renderTags({
          tagsList: selectedValues,
          utils: filter.utils,
          metadata: filter.metadata,
          onDelete: (e, v) => handleDeleteChip(v),
        }),
      [selectedValues, filter.utils, filter.metadata, handleDeleteChip]
    );

    const renderedInput = useMemo(
      () =>
        renderInput({
          field: filter.field!,
          search: searchInputValue,
          onChange: (v) => handleSearch(v),
          onFocus: () => {
            setFocused(true);
            openDropdown();
          },
          onKeyDown: handleKeyDown,
          placeholder: filter.metadata?.placeholder,
        }),
      [
        filter.field,
        searchInputValue,
        handleSearch,
        openDropdown,
        handleKeyDown,
        filter.metadata,
      ]
    );

    return (
      <SearchFilterStatusWrapper
        className={classnames('AutocompleteFilterContent', className)}
      >
        <Box
          display={'flex'}
          flexDirection={'column'}
          gap={0.5}
          ref={componentRef}
        >
          {dropdownState.value && (
            <AutocompleteFilterDropdown
              focused={focused}
              debouncedSearchQuery={debouncedSearchQuery}
              searchInputValue={searchInputValue}
              selectedValues={selectedValues}
              onChangeSelection={onChangeSelection}
              handleCommaSeparatedChangeSelection={noopCommaSelection}
              hasComma={false}
              {...filter}
            />
          )}

          <Box sx={SX.getWrapperSx(focused)}>
            <Box sx={SX.contentColumn}>
              {!!selectedValues.length && (
                <Box sx={SX.tagsWrapper}>{renderedTags}</Box>
              )}
              {renderedInput}
            </Box>
            <Box sx={SX.chevronColumn}>
              <Box sx={SX.chevronBox} onClick={handleClickChevron}>
                <Icon
                  name={dropdownState.value ? 'chevron-up' : 'chevron-down'}
                  color={focused ? 'primary' : 'grey.500'}
                  size={16}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        {exactMatch?.enabled && (
          <FilterExactMatch
            field={filter.field!}
            default={exactMatch.default}
            customOptions={exactMatch.customOptions}
          />
        )}
      </SearchFilterStatusWrapper>
    );
  }
);

AutocompleteFilterContent.displayName = 'AutocompleteFilterContent';
